import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react'

import withConfig from '../../../wrappers/withConfig'
import { AppStateContext } from '../AppStateContext'
import { ParameterContext } from '../../../wrappers/ParameterContext'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'
import { timeWindowOptions } from '../../../../utils/chart/timeWindow'
import toast from '../../../elem/Toast'
import { generateDateParams } from '../../../../utils/chart/values'
import { filterMultiSeriesTimeData, /*filterMultiSeriesTimePeriod*/  } from '../../../../utils/chart/timeWindow'

const DataContext = createContext(null)

export default withConfig(({ config, children }) => {
    const { mapState } = useContext(AppStateContext)
    const { params } = useContext(ParameterContext)
    const { authenticatedFetch } = useContext(APIRequestContext)
    const [chartData, setChartData] = useState([])
    const [paramList, setParamList] = useState([])
    const [tooManyFeatures, setTooManyFeatures] = useState(false)
    const [timeWindow, setTimeWindow] = useState(timeWindowOptions[0])
    const [displayTimeWindowDropdown, toggleTimeWindowDropdown] = useState(
        false
    )
    const [zoomTrigger, setZoomTrigger] = useState(false)
    const [selectedParams, setSelectedParams] = useState([])
    const [
        displayParamWindowDropdown,
        toggleParamWindowDropdown,
    ] = useState(false)

    const [selectedUnits, setSelectedUnits] = useState(null)

    const dateParams = useMemo(() => generateDateParams(params, 'sample', 'SimpleSampleDate'), [params])
    const [StartDate, EndDate] = dateParams

    const [loading, setLoading] = useState(false)
    const { selectedFeatures } = mapState
    const { API_URL } = config

    const [isCollapsed, setCollapsed] = React.useState(false)
    const [resetExpanded, toggleResetExpanded] = useState(false)
    const [chartVisibleRange, setChartVisibleRange] = useState({ left: null, right: null })

    const fetchParamList = useCallback(() => {
        return new Promise(async (resolve, reject) => {

            // get the ids of the selected features
            const facilityIDs = encodeURI(
                selectedFeatures.map(x => x.get('FacilityID')).toString()
            )

            await authenticatedFetch(`${API_URL}/aggregateParam/getParamList`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                body: JSON.stringify({
                    facilityIDs,
                    StartDate,
                    EndDate
                }),
            })
            .then(async response => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then(response => {
                setParamList(response.paramListData)
                return resolve(response.paramListData)
            })
            .catch(e => {
                toast({
                    level: 'error',
                    message:
                        'Sample List: ' +
                        (e.message
                            ? e.message
                            : 'Unable to connect to the server. Please try again later.'),
                })
                return reject()
            })
        })
    }, [selectedFeatures, params])

    const fetchChartData = useCallback((paramList) => {
        return new Promise(async (resolve, reject) => {

            const facilityIDs = encodeURI(
                selectedFeatures.map(x => x.get('FacilityID')).toString()
            )    
            const body = JSON.stringify({
                facilityIDs,
                paramList,
                StartDate,
                EndDate
            })
            await authenticatedFetch(`${API_URL}/aggregateParam`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                body,
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(response => {
                    setChartData(response.chartData)
                    return resolve(response.chartData)
                })
                .catch(e => {
                    toast({
                        level: 'error',
                        message:
                        'Sample List: ' +
                        (e.message
                            ? e.message
                            : 'Unable to connect to the server. Please try again later.'),
                        })
                    return reject()
                })
        })
    }, [selectedFeatures, StartDate, EndDate])
     
    const updateSelectedParams = useCallback(paramList => {
        // if there are selected analytes and all of them are in the new list of analytes,
        // do not update the analyte list
        if (
            selectedParams.length && 
            selectedParams.every(x => paramList.map(y => y.ParamID).includes(x.ParamID))
        ) {
            return false
        } else {
            // otherwise, update the analyte list with the first entry in the list
            const firstParam = paramList[0]
            if (firstParam) {
                setSelectedParams([firstParam])
                return true
            } else {
                setSelectedParams([])
                setChartData([])
                setParamList([])
                return false
            }
        }
    }, [selectedParams, setSelectedParams])

    // when the selected features change or the time window changes, go fetch the list of
    // available analytes
    useEffect(() => {
        if (selectedFeatures.length) {
            // SQL Server has a limit of 2100 parameters that can be supplied
            // to a query. if there is a workaround, this check can be removed
            if (selectedFeatures.length < 2100) {
                setLoading(true)
                setTooManyFeatures(false)
                fetchParamList().then(async paramList => {
                    const paramListChanged = updateSelectedParams(paramList)
                    // if the analyte list did not change, we should fetch new data
                    if (!paramListChanged) {
                        await fetchChartData(paramList)
                            .finally(() => setLoading(false))
                    }
                    // setLoading(false)
                    // return
                })
                .finally(() => setLoading(false))
            } else {
                setTooManyFeatures(true)
            }
        } else {
            // if there are no features,
            // set data to an empty array
            setChartData([])
            setParamList([])
            setSelectedParams([])
        }
    }, [selectedFeatures, dateParams])

    // when the selected analytes change, go fetch the new chart data
    useEffect(() => {
        if (selectedParams && selectedParams.length) {
            setLoading(true)
            fetchChartData(selectedParams)
                .then(() => setLoading(false))
                .catch(e => setLoading(false))
        } else {
            setChartData([])
        }
    }, [selectedParams])

    const paramChartData = useMemo(() => {
        if (chartData && chartData.length) {
            return dateParams
                ? chartData
                // ? filterMultiSeriesTimePeriod(
                //       chartData,
                //       dateParams,
                //       'DateString'
                //   )
                : filterMultiSeriesTimeData(
                      chartData,
                      timeWindow,
                      'DateString'
                  )
        }
        return []
    }, [chartData, timeWindow, dateParams])

    const toggleSelectedParam = useCallback(
        param => {
            if (selectedParams.find(x => x.ParamID === param.ParamID)) {
                setSelectedParams(
                    selectedParams.filter(x => x.ParamID !== param.ParamID)
                )
            } else {
                if (selectedParams.length < 5) {
                    const unit = param.Units
                    if (selectedUnits && unit !== selectedUnits) {
                        toast({
                            level: 'info',
                            message: `Please select a Parameter that has the same units (${selectedUnits}) as the other Parameters in the chart.`,
                            alert: true,
                        })
                    } else {
                        setSelectedParams(selectedParams.concat(param))
                    }
                } else {
                    toast({
                        level: 'info',
                        message:
                            'Too many Parameters selected. Select up to 5 Parameters to display.',
                        alert: true,
                    })
                }
            }
        }, [selectedParams, selectedUnits]
    )

    useEffect(() => {
        if (selectedParams && selectedParams.length && !selectedParams.every(x => x === undefined)) {
            setSelectedUnits(selectedParams[0].Units)
        } else {
            setSelectedUnits(null)
        }
    }, [selectedParams])

    const resetParams = useCallback(() => {
        const firstParam = paramList[0]
        if (firstParam) {
            setSelectedParams([firstParam])
        } else {
            setSelectedParams([])
        }
    }, [paramList])

    const resetZoom = useCallback(() => {
        setZoomTrigger(!zoomTrigger)
    }, [zoomTrigger])

    return (
        <DataContext.Provider
            value={{
                loading,
                chartData,
                tooManyFeatures,
                timeWindow,
                setTimeWindow,
                displayTimeWindowDropdown,
                toggleTimeWindowDropdown,
                selectedParams,
                setSelectedParams,
                toggleSelectedParam,
                toggleParamWindowDropdown,
                displayParamWindowDropdown,
                selectedUnits,
                paramChartData,
                paramList,
                resetParams,
                resetZoom,
                zoomTrigger,
                isCollapsed,
                setCollapsed,
                resetExpanded,
                toggleResetExpanded,
                chartVisibleRange,
                setChartVisibleRange,
                dateParams,
            }}
        >
            {children}
        </DataContext.Provider>
    )
})

export { DataContext }
